// ============================================================
// attack/outcome - writing an attack that dealt no hits.
//
// A miss and a jam both end the attack with one `attack-result` entry,
// and both carry the countdown the attack computed on the way in: its
// narrative, its regeneration and its new conditions land in the SAME
// tab write as the result, so the log and the target cannot disagree.
// ============================================================
import { noiseOf, signatureOf } from '../../engine/stealth/noise';
import type { BeatKindFacts } from './beatFacts';
import { attackBeat } from '../../engine/attackBeat';
import { alerted } from '../../engine/unaware';
import type { CountdownResult } from '../../engine/countdown';
import type { NarrativeEntry, Slot, Tab } from '../../types';
import type { AttackContext } from './context';

/** Fold a countdown and the entry that closes the attack into one tab. */
function withCountdown(t: Tab, cd: CountdownResult, entry: NarrativeEntry): Tab {
  const wAfter = cd.regenHealed
    ? Math.min(t.target.wounds.current + cd.regenHealed, t.target.wounds.max)
    : t.target.wounds.current;
  return {
    ...t,
    target: { ...t.target, wounds: { ...t.target.wounds, current: wAfter }, conditions: cd.newConds },
    narrativeLog: [...t.narrativeLog, ...cd.narrativeEntries, entry],
  };
}

/**
 * Write the entry that ends a hitless attack, with its countdown if any.
 *
 * With a countdown the entries are synced one by one and the tab written
 * once; without, the ordinary narrative path does both. Either way the
 * action is marked for history.
 */
export function writeHitlessOutcome(
  ctx: AttackContext,
  tabId: string,
  entry: NarrativeEntry,
  cd: CountdownResult | null,
): void {
  if (cd) {
    cd.narrativeEntries.forEach((e) => ctx.syncNarrativeToFirestore(e));
    ctx.syncNarrativeToFirestore(entry);
    ctx.updateTab(tabId, (t) => withCountdown(t, cd, entry));
  } else {
    ctx.addNarrative(tabId, entry);
  }
  ctx.historyTrack.current = true;
}

/** Everything a miss entry records - the normal miss, a declined or failed re-roll, a missed bonus attack. */
export interface MissRecord {
  tabId: string;
  attackRoll: number;
  modifiedSkill: number;
  dof: number;
  slot: Slot;
  /** Carried by REFERENCE into the entry: a scattering blast appends its detonation lines after the write. */
  notes: string[];
  overheatDmg: number;
  cdResult: CountdownResult | null;
  targetName: string;
  woundsAfter: number;
  woundsMax: number;
  /** The attack's kind and rate for the beat, as the swing read them (a bonus attack's single). */
  beatKind: BeatKindFacts;
}

/** Write the miss narrative (used by the miss path, the re-roll prompt and the bonus attack). */
export function writeMissNarrative(ctx: AttackContext, miss: MissRecord): void {
  const at = Date.now();
  const missEntry: NarrativeEntry = {
    type: 'attack-result' as const,
    result: {
      success: false,
      attackRoll: miss.attackRoll,
      modifiedSkill: miss.modifiedSkill,
      dos: 0,
      dof: miss.dof,
      totalHits: 0,
      hits: [],
      hitsAfterDefence: [],
      specialNotes: miss.notes,
      overheatDmg: miss.overheatDmg,
    },
    slotName: miss.slot.weaponName,
    fireMode: ctx.fireMode,
    targetName: miss.targetName,
    totalDamage: 0,
    woundsAfter: miss.woundsAfter,
    woundsMax: miss.woundsMax,
    timestamp: at,
    beat: attackBeat(ctx.actorId, miss.slot.weaponType === 'melee', 'miss', miss.tabId, at, {
      noise: noiseOf(miss.slot),
      signature: signatureOf(miss.slot),
      specials: miss.slot.specialRules,
      looks: miss.slot.looks,
      ...miss.beatKind,
    }),
  };
  writeHitlessOutcome(ctx, miss.tabId, missEntry, miss.cdResult);
}

/**
 * THE ATTACK IS OVER. Two things happen exactly once per attack, whichever
 * road it took (the swing itself, or the answer to a re-roll prompt):
 *
 * - whoever was unaware is not any more. A FILTER over whatever the attack
 *   wrote, and LAST: the countdown's write is absolute and was computed with
 *   the condition still on, so stripping it earlier would have it written
 *   back. An unaware target gets no reaction prompt, so nothing of this
 *   attack is still to come;
 * - `onAttackResolved` is told how it came out (`report` null for a jam:
 *   the weapon did not go off, and there is nothing to hear).
 */
export function settleAttack(
  ctx: AttackContext,
  tabId: string,
  report: { hit: boolean; slot: Slot; isMelee: boolean } | null,
): void {
  const tab = ctx.tabs.find((t) => t.id === tabId);
  if (tab && alerted(tab.target.conditions).was) {
    ctx.updateTab(tabId, (t) => ({ ...t, target: { ...t.target, conditions: alerted(t.target.conditions).conds } }));
  }
  if (report) ctx.onAttackResolved?.(report);
}
